<template>
  <div class="row">
    <model-index
      ref="table"
      class="col-12"
      :loading="loading"
      v-bind="$props"
      :columns="columns"
      :busy.sync="busy"
      :models-paginated="modelsPaginated"
      :models="models"
      :page.sync="page"
      :per-page.sync="perPage"
      :from="from"
      :total="totalFiltered"
      :search.sync="search"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :empty-state-title="$t('sale_notes.empty_state.title')"
      empty-state-subtitle=" "
      @show-click="handleShow"
    >
      <template #head-balancing>
        <div>{{ $t('balancings.nominal_header') }}<br><i class="text-muted">{{ $t('balancings.nominal_subheader') }}</i></div>
      </template>
    </model-index>
  </div>
</template>

<script>
import ModelChildIndex from "../../models/children/index";
import {currencyFormatter} from "@/helpers/ui/formatters";

export default {
  extends: ModelChildIndex,
  props: {
    cardTitle: {
      default() {
        return this.lodash.startCase(this.$t('balancings.plural'));
      }
    },
    resourceRoute: { default: "sale_notes" },
    modelVariable: { default: "sale_note" },
    tableMinHeight: { type: Number, default: 85.5 }, // 42.5 + 43
    tableHeight: { type: Number, default: 214.5 }, // 42.5 + (43 * 4)
    denyCreate: { type: Boolean, default: true },
    hideTableHeader: { type: Boolean, default: true },
  },
  data() {
    return {
      sortBy: "id",
      sortDesc: true,
    };
  },
  computed: {
    columns() {
      return {
        ["sale_note"]: [
          {
            key: "balancing_transaction.id",
            label: this.lodash.startCase(this.$t("balancings.id")),
            thStyle: { minWidth: "100px" },
            component: {
              props: ["item"],
              template: '<div>{{ item.balancing_transaction ? item.balancing_transaction.id : "-" }}</div>'
            }
          },
          {
            key: "id",
            label: this.lodash.startCase(this.$t("sale_notes.id")),
            thStyle: { minWidth: "150px" },
            component: {
              props: ["item"],
              computed: {
                customLabel() {
                  return `${this.item.type_name}-${this.item.id}`
                }
              },
              template: '<router-link :to="`/sale_notes/${item.id}`">{{ customLabel }}</router-link>'
            }
          },
          {
            key: "ordered_at",
            label: this.lodash.startCase(this.$t(`sale_notes.sales_ordered_at`)),
            thStyle: { minWidth: "120px" },
            formatter: value => (value ? this.$moment(value).format("DD MMM YYYY") : "-")
          },
          {
            key: "customer.name",
            label: this.lodash.startCase(this.$t("sale_notes.partner")),
            thStyle: { minWidth: "150px" },
            component: {
              props: ["item"],
              template: '<div :title="(item.customer || {}).name">{{ (item.customer || {}).name || "-" }}</div>'
            }
          },
          {
            key: "jala_margin",
            label: this.lodash.startCase(this.$t("sale_notes.resumes.JALA_margin")),
            thStyle: { minWidth: "150px" },
            component: {
              props: ["item"],
              computed: {
                jalaMargin() {
                  return (this.item.sale_items || []).reduce((carry, { margin }) => carry + (margin ?? 0), 0)
                }
              },
              template: '<div>{{ $numeral(jalaMargin).format("$ 0,0.[00]") }}</div>'
            }
          },
          {
            key: "balancing",
            label: this.lodash.startCase(this.$t("sale_notes.resumes.balancing")),
            thStyle: { minWidth: "200px" },
            component: {
              props: ["item"],
              computed: {
                partnerDebt() {
                  if ((this.item.profit_share?.total ?? 0) >= 0) return 0
                  return -this.item.profit_share.total
                },
                partnerMargin() {
                  return this.item.balancing_transaction?.total_debt ?? 0
                },
                label() {
                  return this.partnerDebt
                      ? this.$t('sale_notes.resumes.partner_debt')
                      : this.$t('sale_notes.resumes.transfer_to_partner')
                },
                amount() {
                  return this.partnerDebt || this.partnerMargin
                },
                textClass() {
                  return this.partnerDebt
                      ? 'text-danger'
                      : { 'text-success': this.amount > 0, 'text-danger': this.amount < 0 }
                }
              },
              methods: {
                currencyFormatter,
              },
              template: `
                <div :class="textClass">
                  <div style="font-size: 11px; text-transform: uppercase">{{ label }}</div>
                  <b>{{ currencyFormatter(amount) }}</b>
                </div>
              `
            }
          },
          {
            key: "balancing_approval_status",
            label: `Status ${this.lodash.startCase(this.$t("balancing_approvals.singular"))}`,
            thStyle: { minWidth: "160px" },
            component: {
              props: ["item"],
              template: `
                <status-badge
                    v-if="item.balancing_approval_status"
                    entity="balancing_approval"
                    type="status"
                    :status="item.balancing_approval_status"
                ></status-badge>
                <span v-else>-</span>
              `
            }
          },
        ]
      };
    }
  },
  methods: {
    fetchModels(page = 1) {
      return this.$axios
          .get("/sale_orders", {
            params: {
              page: page,
              per_page: this.perPage,
              search: this.search,
              sort: `${this.sortBy},${this.sortDesc ? "desc" : "asc"}`,
              with: [
                "customer",
                "sale_items",
                "balancing_transaction",
                "profit_share",
                "balancing_approvals.histories.performed_by",
                "balancing_approvals.approval_template.approval_template_steps.approver",
              ].join(","),
              sales_plan_id: this.parent.id || this.$route.params.key,
              type__not_in: ["harvest"],
              has_balancing_transaction: 1,
              ...this.filterParams
            }
          })
          .then(response => {
            const {
              data: { data, meta }
            } = response;

            this.models = data.map(datum => ({
              ...datum,
              _denyShow: true,
              _denyEdit: true,
              _denyDelete: true,
            }));
            this.page = Number(meta.current_page);
            this.perPage = Number(meta.per_page);
            this.from = Number(meta.from);
            this.total = Number(meta.total);

            this.stats = [];

            return response;
          })
          .catch(error => {
            this.failedAlert(error);
            return null;
          });
    },
  }
};
</script>
<style lang="scss" scoped>
.text-muted {
  color: #667085;
}

</style>
